import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { api } from '@/app/api';
import { fetchSettings, selectSettingsGroup } from '@main/features/settings/settingsSlice';
import { UiButton, UiDateRangePicker, UiInput, UiNativeSelect } from '@shared/common/ui';

function fmtAmount(value) {
  return new Intl.NumberFormat('ru-RU').format(value ?? 0) + ' ₽';
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function today() {
  return toIsoDate(new Date());
}

function currentMonthStart() {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message ?? error?.message ?? fallback;
}

/**
 * Общие расходы кабинета — те же поля, что в CRM «Список расходов»,
 * но запись не принадлежит ни одному филиалу.
 */
export function OwnerCommonExpensesPage() {
  const dispatch = useDispatch();
  const groups = useSelector(selectSettingsGroup('expenditure'));
  const [from, setFrom] = useState(currentMonthStart());
  const [to, setTo] = useState(today());
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (groups.status === 'idle') dispatch(fetchSettings('expenditure'));
  }, [dispatch, groups.status]);

  const listParams = useMemo(
    () => ({
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [from, to],
  );

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const { data } = await api.get('/owner/common-expenditures', { params: listParams });
      setItems(data.items ?? []);
      setStatus('ready');
    } catch (err) {
      setItems([]);
      setError(errorMessage(err, 'Не удалось загрузить общие расходы'));
      setStatus('error');
    }
  };

  useEffect(() => {
    void load();
  }, [from, to]);

  const total = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.amount ?? 0), 0),
    [items],
  );

  return (
    <section className="expenditures-page">
      <header className="page-header">
        <h1>Общие расходы</h1>
        <div className="page-header__spacer" />
        <div className="expenditures-page__toolbar">
          <UiDateRangePicker
            from={from}
            to={to}
            onChange={({ from: nextFrom, to: nextTo }) => {
              setFrom(nextFrom);
              setTo(nextTo);
            }}
          />
          <UiButton
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => {
              setFrom(currentMonthStart());
              setTo(today());
            }}
          >
            Текущий месяц
          </UiButton>
        </div>
        <UiButton type="button" onClick={() => setEditing({})}>
          Новый расход
        </UiButton>
      </header>

      <p className="muted" style={{ marginTop: 8 }}>
        Расходы без филиала: онлайн-касса, общие сервисы и всё, что относится ко всему кабинету.
        Они входят в виджет «Расходы» на странице профиля.
      </p>

      {editing !== null && (
        <CommonExpForm
          item={editing}
          groupItems={groups.items ?? []}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {error ? <div className="banner banner--danger" style={{ marginTop: 16 }}>{error}</div> : null}

      <div className="form-card expenditures-page__table-card" style={{ marginTop: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Название</th>
              <th>Группа</th>
              <th style={{ textAlign: 'right' }}>Сумма</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.spentAt ?? '—'}</td>
                <td>{item.name ?? '—'}</td>
                <td>{item.groupName ?? item.groupId ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtAmount(item.amount)}</td>
                <td style={{ textAlign: 'right' }}>
                  <UiButton variant="ghost" size="sm" type="button" onClick={() => setEditing(item)}>
                    Изменить
                  </UiButton>
                  <UiButton
                    variant="danger"
                    size="sm"
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Удалить расход?')) return;
                      try {
                        await api.delete(`/owner/common-expenditures/${item.id}`);
                        void load();
                      } catch (err) {
                        setError(errorMessage(err, 'Не удалось удалить расход'));
                      }
                    }}
                  >
                    Удалить
                  </UiButton>
                </td>
              </tr>
            ))}
            {!items.length && status !== 'loading' && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                  Пока нет общих расходов
                </td>
              </tr>
            )}
            {status === 'loading' && (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                  Загрузка…
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 500 }}>
                  Итого:
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtAmount(total)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function CommonExpForm({ item, groupItems, onCancel, onSaved }) {
  const isNew = !item.id;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: item.name ?? '',
      amount: item.amount ?? '',
      spentAt: item.spentAt ?? today(),
      groupId: item.groupId ?? '',
    },
  });

  const onSubmit = async (values) => {
    const payload = {
      name: values.name.trim() || null,
      amount: Number(values.amount),
      spentAt: values.spentAt,
      groupId: values.groupId.trim() || null,
    };
    setSaving(true);
    setSaveError('');
    try {
      if (isNew) await api.post('/owner/common-expenditures', payload);
      else await api.patch(`/owner/common-expenditures/${item.id}`, payload);
      onSaved();
    } catch (err) {
      setSaveError(errorMessage(err, 'Не удалось сохранить расход'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="form-card" onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 16 }}>
      <h3>{isNew ? 'Новый общий расход' : 'Редактирование'}</h3>
      <div className="form-grid">
        <div className="field">
          <label>Дата</label>
          <UiInput type="date" {...register('spentAt', { required: 'Обязательно' })} />
          {errors.spentAt && <span className="form-error">{errors.spentAt.message}</span>}
        </div>
        <div className="field">
          <label>Сумма</label>
          <UiInput
            type="number"
            step="0.01"
            {...register('amount', { required: 'Обязательно', valueAsNumber: true })}
          />
          {errors.amount && <span className="form-error">{errors.amount.message}</span>}
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Название</label>
          <UiInput {...register('name')} />
        </div>
        <UiNativeSelect label="Группа" className="field" {...register('groupId')}>
          <option value="">Без группы</option>
          {groupItems.map((group) => (
            <option key={group.key} value={group.key}>
              {group.value?.name ?? group.key}
            </option>
          ))}
        </UiNativeSelect>
      </div>

      {saveError && <div className="form-error">{saveError}</div>}

      <div className="form-actions">
        <UiButton type="submit" disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </UiButton>
        <UiButton variant="ghost" type="button" onClick={onCancel}>
          Отмена
        </UiButton>
      </div>
    </form>
  );
}
